import React, { useEffect, useRef, useState } from "react";
import { CentralLoadingIndicator, PetAvatars, ReasonTypeSelectionList } from "../../../components";
import { useAppointmentStore } from "../../../hooks/redux-hooks/appointment-store/appointment.store";
import ReasonForVisitCardList from "../../modals/ExpressServiceUpsellModal/ReasonForVisitCardList";
import { Box } from "@mui/material";
import DoctorVisitToggleCard from "./DoctorVisitToggleCard";
import DropOffToggleCard from "./DropOffToggleCard";
import { CREATE_BOOKING_TYPE } from "../../../config/params";
import { useAllServiceStatusStore, useBookingAppointmentTypeStore, useBookingReasonsNavigation } from "../../../hooks";
import UrgentVisitToggleCard from "./UrgentVisitToggleCard";
import WellnessReasonIcon from "../../../components/Icons/WellnessReasonIcon";
import SickReasonsIcon from "../../../components/Icons/SickReasonsIcon";
import { ExpressServiceIcon, InfoIcon } from "../../../components/Icons";
import { useUpdatedReasonForVisit } from "../../../services/updated-reason-for-visit/updated-reason-for-visit.service";
import { useSocDatByPetReminderCard } from "../../../services/Standard-of-care/standard-of-care.service";
import { MIXPANEL_EVENTS } from "../../../config/mixPanelEvents";
import { formatReasonPetNames } from "../../../utils/bookingFlow";

interface IReasonForVisitContainer {
setIsLoading?: (isLoading: boolean) => void;
setActiveStep: (step: number) => void;
handleMixPanelLog: (event: string, referredFromPage?: string) => void;
}

const ReasonForVisitContainer = ({
  setIsLoading,
  setActiveStep,
  handleMixPanelLog,
}: IReasonForVisitContainer) => {
  const { updatedReasonList, loading } = useUpdatedReasonForVisit();
  const { selectedPets, setAppointmentsForm } = useAppointmentStore();
  const petAvatarRef = useRef<HTMLDivElement>(null);
  const { bookingAppointmentType, setDefaultBookingType } = useBookingAppointmentTypeStore();
  const [visitNote, setvisitNote] = useState("");
  const { isDropOffServiceAvailable, isIntegratedServiceAvailable }=useAllServiceStatusStore();
  const { getSocDataByPetsForReminderCard, socDataByPet, isLoading } = useSocDatByPetReminderCard();
  const [socDataAvailable, setSocDataAvailable] = useState<boolean>(false);
  const {
    petsReasonSelection,
    currentNavigationIndex,
    setPetsReasonSelection,
    setDefaultSelectionValues,
  }=useBookingReasonsNavigation();

  const handleChange = (visitNote: string) => {
    setvisitNote(visitNote);
  };
  const onReasonSelection = (reason: string) => {
    const newSelectedReasons = petsReasonSelection[currentNavigationIndex]?.selectedReasons.includes(reason) ?
      petsReasonSelection[currentNavigationIndex]?.selectedReasons.filter((r) => r !== reason) :
      [...petsReasonSelection[currentNavigationIndex]?.selectedReasons, reason];

    const updatedPetReasonSelection = petsReasonSelection.map((pet, index) =>
      index === currentNavigationIndex ? { ...pet, selectedReasons: newSelectedReasons,
        currentSelectionState: 1 as 1 } : pet,
    );

    setPetsReasonSelection(updatedPetReasonSelection);
  };

  const handleReasonCategorySelection = (reasonCategory: string) =>{
    const updatedPetReasonSelection = petsReasonSelection.map((pet, index) =>
      index === currentNavigationIndex ?
        (petsReasonSelection[currentNavigationIndex]?.selectedReasonCategory.length &&
        (petsReasonSelection[currentNavigationIndex]?.selectedReasonCategory !== reasonCategory)) ?
          { ...pet, selectedReasonCategory: reasonCategory, selectedReasons: [], additionalDescription: "" } :
          { ...pet, selectedReasonCategory: reasonCategory } : pet,
    );

    setPetsReasonSelection(updatedPetReasonSelection);
  };

  const handleDropOffToggle = ()=>{
    setActiveStep(3);
    handleMixPanelLog(MIXPANEL_EVENTS.USER_SWITCH_TO_DROP_OFF, "reason for visit category screen");
  };

  const handleUrgentVisitToggle = ()=>{
    setActiveStep(5);
  };

  const handleDoctorVisitToggle = ()=>{
    setAppointmentsForm(null);
    setDefaultSelectionValues();
    setActiveStep(1);
    setDefaultBookingType();
    handleMixPanelLog(MIXPANEL_EVENTS.USER_SWITCH_TO_IN_ROOM_VISIT, "reason for visit category screen");
  };

  const getReasonTypeIcon = ()=>{
    switch (petsReasonSelection[currentNavigationIndex]?.selectedReasonCategory) {
    case "Sick":
      return <SickReasonsIcon />;
    case "Express":
      return <ExpressServiceIcon />;
    default:
      return <WellnessReasonIcon />;
    }
  };

  const handleExpressAvailability = ()=>{
    return selectedPets.filter((pet)=>pet.id === petsReasonSelection[currentNavigationIndex].petId)[0]
      ?.isEligibleForExpressVisit ? false : true;
  };

  const getSocData = (petId: string) => {
    if (socDataByPet) {
      return socDataByPet.find((socData: { animalId: string; }) => socData.animalId == petId)?.items;
    }
  };

  const hasSocData = (petId: string) => {
    const socData = getSocData(petId);
    let hasData = false;

    if (socData) {
      Object.entries(socData).map(([category, data], index) => {
        if (!category.startsWith("_") && data.length && data.some((item: any) => item.status !== "Current")) {
          hasData = true;
        }
      });
    }

    return hasData;
  };

  useEffect(() => {
    if (selectedPets.length) {
      getSocDataByPetsForReminderCard(selectedPets.map((pet: { id: string; }) => pet.id));
    }
  }, [selectedPets]);

  useEffect(() => {
    if (petAvatarRef.current) {
      petAvatarRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }

    if (hasSocData(petsReasonSelection[currentNavigationIndex].petId)) {
      setSocDataAvailable(true);
    } else {
      setSocDataAvailable(false);
    }
  }, [currentNavigationIndex, socDataByPet]);

  useEffect(() => {
    if (petsReasonSelection[currentNavigationIndex]?.additionalDescription) {
      setvisitNote(petsReasonSelection[currentNavigationIndex]?.additionalDescription);
    } else {
      setvisitNote("");
    }
  }, [currentNavigationIndex]);

  useEffect(()=>{
    setIsLoading && setIsLoading(loading);
  }, [loading]);

  useEffect(()=>{
    const updatedPetReasonSelection = petsReasonSelection.map((pet, index) =>
      index === currentNavigationIndex ? { ...pet, additionalDescription: visitNote } : pet,
    );

    setPetsReasonSelection(updatedPetReasonSelection);
  }, [visitNote]);


  useEffect(() => {
    if (socDataAvailable) {
      const updatedPetReasonSelection = petsReasonSelection.map((pet, index) =>
        index === currentNavigationIndex && (pet.selectedReasonCategory === "") ?
          { ...pet, selectedReasonCategory: "Wellness" } : pet,
      );

      setPetsReasonSelection(updatedPetReasonSelection);
    }
  }, [socDataAvailable, currentNavigationIndex]);


  return (
    <div className="w-full flex flex-col gap-4 p-4 border border-taupe-50 rounded-xl ">
      <div className="flex justify-between items-center">
        <div className="flex flex-col gap-1">
          <div className="text-lg">
            Which of these does&nbsp;
            <span className="font-semibold">{formatReasonPetNames([selectedPets.filter((pet)=>pet.id ===
              petsReasonSelection[currentNavigationIndex]?.petId)[0]?.name])}
            </span>  need?
          </div>
          <div className="text-sm text-gray-1">
            {petsReasonSelection[currentNavigationIndex]?.selectedReasonCategory &&
            (petsReasonSelection[currentNavigationIndex]?.currentSelectionState === 1) ?
              "Select all that apply." : "Select one."}
          </div>
        </div>
        <div ref={petAvatarRef}>
          <PetAvatars
            height={50}
            width={50}
            petDetails={[selectedPets.filter((pet)=>pet.id ===
              petsReasonSelection[currentNavigationIndex]?.petId)[0]]} />
        </div>
      </div>
      {loading || isLoading ?
        <CentralLoadingIndicator /> :
        <div className="flex flex-col gap-3">
          {petsReasonSelection.length &&
          petsReasonSelection[currentNavigationIndex]?.currentSelectionState == 0 &&
            <div>
              <ReasonTypeSelectionList
                handleSelection={handleReasonCategorySelection}
                selectedReasonType={petsReasonSelection[currentNavigationIndex].selectedReasonCategory}
                isExpressPresent={handleExpressAvailability() || isIntegratedServiceAvailable}
                hasSocData={socDataAvailable}
                petName={selectedPets.filter((pet)=>pet.id ===
                  petsReasonSelection[currentNavigationIndex]?.petId)[0].name}
              />
              <span className="text-gray-1 text-sm flex gap-1 mt-2 items-center">
                <InfoIcon width={18} height={18} color="#908E8D" />
                Contact your hospital for Surgery appointment</span>
            </div>}
          {petsReasonSelection.length &&
          petsReasonSelection[currentNavigationIndex]?.currentSelectionState == 1 &&
          <div>
            <ReasonForVisitCardList
              selectedReasons={petsReasonSelection[currentNavigationIndex]?.selectedReasons}
              reasonsList={updatedReasonList.filter((reason)=>
                reason.name === petsReasonSelection[currentNavigationIndex]?.selectedReasonCategory).flatMap(
                (category) => category.options)}
              onReasonSelection={onReasonSelection}
              icon={getReasonTypeIcon()}
              hasSocData={socDataAvailable}
            />
            <div className="mx-1 my-4 rounded-lg bg-white border border-taupe-50 p-4">
              <p className="font-semibold text-sm mb-3">Anything else you want to share
                  with us prior to your appointment?</p>
              <div className="relative">
                <textarea
                  value={visitNote}
                  onChange={(e) => handleChange(e.target.value)}
                  placeholder="Let us know here"
                  maxLength={180}
                  className="w-full h-28 border border-taupe-50 rounded-lg p-3
                    resize-none text-sm focus-within:outline-gv-yellow" />
                <span className="absolute right-3 bottom-3 text-xs text-gray-1">{180 - visitNote.length}</span>
              </div>
            </div>
          </div>}
          <Box className="border-b-2 border-gray-3 w-full"></Box>
          {isDropOffServiceAvailable ?
            (bookingAppointmentType === CREATE_BOOKING_TYPE.DROPOFF_BOOKING) ?
              <DoctorVisitToggleCard handleToggle={handleDoctorVisitToggle} />:
              <DropOffToggleCard handleToggle={handleDropOffToggle} /> : null}
          <UrgentVisitToggleCard handleToggle={handleUrgentVisitToggle} />
        </div>}
    </div>
  );
};

export default ReasonForVisitContainer;
